<script setup lang="ts">
import type { EventVault } from "@/models"
import { useQueryClient } from "@tanstack/vue-query"
import { computed, ref } from "vue"
import { useDeleteEventVault } from "@/api"
import { BaseModal, Button, Icon, Input } from "@/components/ui"
import { useToast } from "@/components/ui/ToastReka/useToast"
import { useModal, useTranslation } from "@/composables"
import { DeleteEventModalKey } from "@/symbols"

const props = defineProps<{
  eventVault?: EventVault
}>()

const emit = defineEmits<{
  (e: "success"): void
}>()

const queryClient = useQueryClient()

const { t } = useTranslation()

const toast = useToast()

const { toggle } = useModal(DeleteEventModalKey)

const { mutate: mutateDeleteEventVault, isPending: isPendingDeleteEventVault } = useDeleteEventVault()

const deleteConfirmation = ref("")

const isTitleInvalid = computed(() => deleteConfirmation.value && deleteConfirmation.value !== props.eventVault?.title)

const deleteConfirmationValidation = computed(() => isTitleInvalid.value ? t("delete_event_modal.validation_error") : "")

const disableDelete = computed(() =>
  !deleteConfirmation.value
  || isTitleInvalid.value
  || isPendingDeleteEventVault.value,
)

const hasEventContent = computed(() =>
  props.eventVault?.totalPics
  || props.eventVault?.totalVids
  || props.eventVault?.totalMessages
  || props.eventVault?.totalAlbums,
)

function onClose() {
  deleteConfirmation.value = ""
  toggle()
}

function onDelete() {
  if (isTitleInvalid.value || !props.eventVault?.id)
    return toast.error()

  mutateDeleteEventVault(props.eventVault?.id, {
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["listEventVaults"] })
      toast.success({ description: t("delete_event_modal.success") })
      onClose()
      emit("success")
    },
    onError: () => {
      toast.error()
      onClose()
    },
  })
}
</script>

<template>
  <BaseModal :name="DeleteEventModalKey" data-test="delete-event-modal">
    <div class="bg-white rounded-lg overflow-hidden flex flex-col">
      <div class="shrink-0 bg-white p-6 pb-4 border-b border-gray-200">
        <div class="flex items-start justify-between">
          <div>
            <h1 class="font-heading font-semibold text-lg leading-tight md:text-xl flex gap-2">
              <Icon icon="Caution" class="shrink-0" />
              {{ t("delete_event_modal.title") }}
            </h1>
            <p class="text-sm text-label hidden md:block font-body ml-8">
              {{ t("delete_event_modal.description") }}
            </p>
          </div>
          <Button variant="flat" prefix-icon="Close" class="p-0" data-test="close-button" @click="onClose">
            <span class="sr-only">{{ t("delete_event_modal.close") }}</span>
          </Button>
        </div>
      </div>

      <form class="p-6 pt-4 flex flex-col gap-4 text-sm md:text-base" @submit.prevent="onDelete">
        <div v-if="hasEventContent" class="flex flex-col gap-2" data-test="event-content-summary">
          <p>
            {{ t("delete_event_modal.message") }}
          </p>
          <ul class="list-disc pl-5 flex flex-col gap-1">
            <li v-if="eventVault?.totalPics">
              {{ t("delete_event_modal.message_item_pics", eventVault.totalPics) }}
            </li>
            <li v-if="eventVault?.totalVids">
              {{ t("delete_event_modal.message_item_vids", eventVault.totalVids) }}
            </li>
            <li v-if="eventVault?.totalMessages">
              {{ t("delete_event_modal.message_item_messages", eventVault.totalMessages) }}
            </li>
            <li v-if="eventVault?.totalAlbums">
              {{ t("delete_event_modal.message_item_albums", eventVault.totalAlbums) }}
            </li>
          </ul>
        </div>

        <p v-if="eventVault?.totalPics || eventVault?.totalVids" data-test="download-zip-message">
          {{ t("delete_event_modal.download_zip") }}
        </p>

        <p>
          <i18n-t keypath="delete_event_modal.confirm_message" scope="global">
            <template #title>
              <strong>{{ eventVault?.title }}</strong>
            </template>
          </i18n-t>
        </p>

        <Input
          v-model="deleteConfirmation"
          name="deleteConfirmation"
          data-test="confirmation-input"
          :label="t('delete_event_modal.confirm_label')"
          :placeholder="eventVault?.title"
          :error="deleteConfirmationValidation"
        />
      </form>

      <div class="shrink-0 bg-white border-t border-gray-200 p-6 pt-4">
        <div class="flex justify-end gap-3">
          <Button variant="flat" :disabled="isPendingDeleteEventVault" data-test="cancel-button" @click="onClose">
            {{ t("delete_event_modal.cancel") }}
          </Button>
          <Button variant="danger" :disabled="disableDelete" data-test="delete-button" @click="onDelete">
            <span class="hidden md:block">{{ isPendingDeleteEventVault ? t("delete_event_modal.deleting") : t("delete_event_modal.confirm") }}</span>
            <span class="block md:hidden">{{ isPendingDeleteEventVault ? t("delete_event_modal.deleting") : t("delete_event_modal.confirm_short") }}</span>
            <template #suffixIcon>
              <Icon v-if="isPendingDeleteEventVault" icon="Loading" />
            </template>
          </Button>
        </div>
      </div>
    </div>
  </BaseModal>
</template>
